import React, { useState, useEffect, useRef } from 'react';
import { Eye, Users, Briefcase, Award, Clock, ShieldCheck, MonitorSmartphone, Code, Cpu, LineChart } from 'lucide-react';
import PageHeader from '../../components/PageHeader/PageHeader';
import './About.css';
import serverImage from '../../assets/pexels-daddysky-18082922.jpg';
import cameraImage from '../../assets/pexels-rakesh-teja-540228275-16560413.jpg';
import controlRoomImage from '../../assets/gs7sn9cq.png';
import programmerImage from '../../assets/pexels-yankrukov-9072304.jpg';
import programmerSetupImage from '../../assets/pexels-tima-miroshnichenko-5380666.jpg'; 
import cableInstallerImage from '../../assets/pexels-philips-espinoza-1275989311-26100225.jpg';

const RevealOnScroll = ({ children, className = '', delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (let i = 0; i < entries.length; i++) {
          setIsVisible(entries[i].isIntersecting);
        }
      },
      { threshold: 0.1 }
    );
    const current = domRef.current;
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, []);

  return (
    <div ref={domRef} className={`reveal-wrapper ${isVisible ? 'is-visible' : ''} ${className}`} style={{ transitionDelay: `${delay}s` }}>
      {children}
    </div>
  );
};

const AnimatedCounter = ({ endValue, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (let i = 0; i < entries.length; i++) {
          setIsVisible(entries[i].isIntersecting);
        }
      },
      { threshold: 0.1 }
    );
    const current = domRef.current;
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, []);

  useEffect(() => {
    if (isVisible) {
      let current = 0;
      const increment = endValue / 40; 
      const timer = setInterval(() => {
        current += increment;
        if (current >= endValue) {
          setCount(endValue);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, 50);
      return () => clearInterval(timer);
    } else {
      setCount(0);
    }
  }, [isVisible, endValue]);

  return <span ref={domRef}>{count}{suffix}</span>;
};

const AnimatedProgressBar = ({ title, percentage }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (let i = 0; i < entries.length; i++) {
          setIsVisible(entries[i].isIntersecting);
        }
      },
      { threshold: 0.1 }
    );
    const current = domRef.current;
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, []);

  return (
    <div className="progress-item" ref={domRef}>
      <div className="progress-header">
        <span>{title}</span>
        <span><AnimatedCounter endValue={percentage} suffix="%" /></span>
      </div>
      <div className="progress-track">
        <div className="progress-fill" style={{ width: isVisible ? `${percentage}%` : '0%' }}></div>
      </div>
    </div>
  );
};

export default function About() {
  const stats = [
    { label: 'ACTIVE CLIENTS', value: 52, suffix: '+', icon: <Users size={36} /> },
    { label: 'PROJECTS DONE', value: 150, suffix: '+', icon: <Briefcase size={36} /> },
    { label: 'TEAM EXPERTS', value: 15, suffix: '+', icon: <Award size={36} /> },
    { label: 'YEARS EXPERIENCE', value: 15, suffix: '+', icon: <Clock size={36} /> },
  ];

  const progressBars = [
    { title: 'HARDWARE & RESALE', percentage: 98 },
    { title: 'SECURITY INSTALLATIONS', percentage: 96 },
    { title: 'FIBRE & NETWORKING', percentage: 95 },
    { title: 'WEB & MOBILE DESIGN', percentage: 93 },
  ];

  const coreValues = [
    { num: '01', title: 'Focus Based Organisation', desc: 'We focus on organizational basic needs and strive to exceed the expectations of our clients. Competition is healthy and welcome, as this creates a stable and healthy relationship with our clients and people.', icon: <MonitorSmartphone size={30} /> },
    { num: '02', title: 'Key Personnel And Work Force', desc: 'Singularity Tech consists of a highly motivated, skilled team of security technicians, network engineers, procurement specialists, and developers who are always pushing boundaries.', icon: <LineChart size={30} /> },
    { num: '03', title: 'Summary And Values', desc: 'We are concerned about changing the very way businesses work, by equipping, securing, and powering businesses with premium IT hardware, Solar Solutions, and Interactive Web Applications.', icon: <Code size={30} /> },
    { num: '04', title: 'Our Unique Angle', desc: 'We strive to make technology integrate seamlessly with your business so that your business can grow securely. We are dedicated to delivering the most advanced hardware and technology to our clients.', icon: <Cpu size={30} /> },
  ];

  const statsElements = [];
  for (let i = 0; i < stats.length; i++) {
    const stat = stats[i];
    statsElements.push(
      <RevealOnScroll key={i} delay={i * 0.15}>
        <div className="stat-item">
          <div className="stat-icon-wrap">{stat.icon}</div>
          <h4><AnimatedCounter endValue={stat.value} suffix={stat.suffix} /></h4>
          <p>{stat.label}</p>
        </div>
      </RevealOnScroll>
    );
  }

  const progressElements = [];
  for (let j = 0; j < progressBars.length; j++) {
    const bar = progressBars[j];
    progressElements.push(
      <AnimatedProgressBar key={j} title={bar.title} percentage={bar.percentage} />
    );
  }

  const valueCardElements = [];
  for (let k = 0; k < coreValues.length; k++) {
    const val = coreValues[k];
    valueCardElements.push(
      <RevealOnScroll key={k} delay={k * 0.1}>
        <div className="value-card">
          <div className="value-card-bg-number">{val.num}</div>
          <h3>{val.title}</h3>
          <p>{val.desc}</p>
          <div className="value-card-icon-area">{val.icon}</div>
        </div>
      </RevealOnScroll>
    );
  }

  return (
    <div className="about-page">
      <PageHeader 
        title={<>Architecting Secure <br/><span>& Equipped Environments</span></>}
        description="Singularity Tech is a premier ICT solutions provider. We supply enterprise-grade IT hardware, deploy advanced physical security infrastructure, high-speed fibre networks, and custom software to safeguard and streamline your operations."
      />

      <div className="about-content">
        <div className="container about-grid">
          <div className="about-text-container">
            <RevealOnScroll>
              <span className="section-label">WHO WE ARE</span>
              <h2>Pioneers in Hardware, Security & Infrastructure</h2>
              <p className="lead-text">
                Singularity Tech was founded on the core principle that robust infrastructure and premium equipment are the foundation of every successful modern enterprise. We specialize in the supply of top-tier IT hardware from brands like Dell, HP, Lenovo, and Huawei, alongside the installation of physical security systems and high-speed fibre optic networks.
              </p>
              <p className="lead-text">
                Our dedicated team of procurement specialists, security technicians, and network engineers work in perfect synergy with our software division. While we supply your workstations, secure your physical premises, and ensure energy independence, our developers craft custom web and mobile applications to ensure your digital footprint is just as powerful.
              </p>
            </RevealOnScroll>

            <div className="mission-vision-container">
              <RevealOnScroll delay={0.2}>
                <div className="mission-block">
                  <div className="mission-icon"><ShieldCheck size={32} /></div>
                  <div className="mission-text">
                    <h3>Our Mission</h3>
                    <p>To deliver the most reliable hardware, secure networks, and advanced physical infrastructure solutions. We are dedicated to providing flawless installations and premium IT equipment, enabling our clients to scale their operations safely, efficiently, and without interruption.</p>
                  </div>
                </div>
              </RevealOnScroll>

              <RevealOnScroll delay={0.4}>
                <div className="mission-block">
                  <div className="mission-icon"><Eye size={32} /></div>
                  <div className="mission-text">
                    <h3>Our Vision</h3>
                    <p>To be the leading integrated technology provider and hardware reseller in South Africa, recognized globally for our unwavering commitment to client success, cutting-edge equipment deployments, and superior structural network designs.</p>
                  </div>
                </div>
              </RevealOnScroll>
            </div>
          </div>

          <div className="about-image-collage">
            <RevealOnScroll className="collage-img-wrapper img-tall" delay={0.2}>
              <img src={serverImage} alt="Server" />
            </RevealOnScroll>
            <RevealOnScroll className="collage-img-wrapper img-short" delay={0.4}>
              <img src={programmerImage} alt="Programmer" />
            </RevealOnScroll>
            <RevealOnScroll className="collage-img-wrapper img-short" delay={0.6}>
              <img src={cameraImage} alt="Close up of secure network data processing" />
            </RevealOnScroll>
            <RevealOnScroll className="collage-img-wrapper img-wide" delay={0.8}>
              <img src={controlRoomImage} alt="Control Room" />
            </RevealOnScroll>
            <RevealOnScroll className="collage-img-wrapper img-wide" delay={0.8}>
              <img src={programmerSetupImage} alt="Programmer Setup" />
            </RevealOnScroll>
          </div>
        </div>
      </div>

      <div className="service-index-section">
        <div className="container service-index-grid">
          <div className="service-index-text">
            <RevealOnScroll>
              <span className="section-label">SERVICE INDEX</span>
              <h2>Equipping and Innovating with Tech Trends</h2>
              <p>We focus on sourcing premium hardware and developing your security and digital needs. We have dedicated teams tailored to your specific procurement, infrastructure, and development requirements.</p>
            </RevealOnScroll>
            <div className="progress-bars-container">
              {progressElements}
            </div>
          </div>
          <RevealOnScroll className="service-index-image" delay={0.3}>
            <img src={cableInstallerImage} alt="Cable Installer" />
          </RevealOnScroll>
        </div>
      </div>

      <div className="core-values-section">
        <div className="container">
          <RevealOnScroll>
            <span className="section-label">OUR VALUES</span>
            <h2 style={{ fontSize: '36px', fontWeight: 'bold', color: 'var(--text-dark)' }}>What Drives Us</h2>
          </RevealOnScroll>
          <div className="core-values-grid">
            {valueCardElements}
          </div>
        </div>
      </div>

      <div className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {statsElements}
          </div>
        </div>
      </div>
      
    </div>
  );
}